import { useMarkerVisibility } from 'hooks/map/useMarkerVisibility';
import { isEqual, orderBy, sortBy } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { NodeApi, TreeApi } from 'react-arborist';
import { CtxEdit } from 'session/ctxEdit';

export interface LayerTreeNode {
    id: string;
    index: number;
}

export const useLayerPanel = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const treeRef = useRef<TreeApi<LayerTreeNode>>();

    const ctxEdit = useContext(CtxEdit);
    const markerVisibility = useMarkerVisibility();

    const worldMarkers = ctxEdit.worldMarkers.filter((e) =>
        markerVisibility.isMarkerVisibleinLayerList(e)
    );
    const orderedMarkers = orderBy(worldMarkers ?? [], ['order'], ['desc']);

    function createInitialData() {
        return orderedMarkers.map((e, i) => ({ id: e.id, index: i }));
    }

    const [treeData, setTreeData] = useState(createInitialData());
    const [height, setHeight] = useState(0);
    const [localSelectedMarkers, setLocalSelectedMarkers] = useState(ctxEdit.selectedMarkers);

    function onMove(moveParams: { dragIds: string[]; index: number }) {
        const { dragIds, index } = moveParams;
        const [dragId] = dragIds;
        setTreeData((treeData) => {
            const originalIndex = treeData.findIndex((e) => e.id === dragId);
            const newArr = [...treeData];
            if (index > originalIndex) {
                newArr.splice(index, 0, treeData[originalIndex]);
                newArr.splice(originalIndex, 1);
            } else {
                newArr.splice(originalIndex, 1);
                newArr.splice(index, 0, treeData[originalIndex]);
            }

            const newData = newArr.map((e, i) => ({
                worldMapMarkerId: e.id,
                order: newArr.length - i
            }));
            ctxEdit.reorderMarkers(newData);

            return newArr;
        });
    }

    function onSelect(nodes: NodeApi<LayerTreeNode>[]) {
        // Let's not update when it's empty.
        // That situation can happen when a new marker is created
        if (nodes.length) {
            const newMarkers = nodes.map((e) => e.id);
            const areEqual = isEqual(sortBy(newMarkers), sortBy(localSelectedMarkers));
            if (!areEqual) {
                setLocalSelectedMarkers(newMarkers);
                ctxEdit.setSelectedMarkers(newMarkers);
            }
        }
    }

    function itemOnKeyDown(e: React.KeyboardEvent) {
        // Stop propagating shortcuts to the tree
        e.stopPropagation();
    }

    function itemOnClick(e: React.MouseEvent, markerId: string) {
        // This implements multi-select in a custom way
        // The default one works well on OSX but on windows
        // it works with WIN key so this reimplements it to CTRL
        const isCtrl = e.metaKey || e.ctrlKey;
        if (isCtrl) {
            treeRef.current.selectMulti(markerId);
            e.stopPropagation();
        }
    }

    useEffect(() => {
        const areEqual = isEqual(sortBy(ctxEdit.selectedMarkers), sortBy(localSelectedMarkers));
        if (!areEqual) {
            setLocalSelectedMarkers(ctxEdit.selectedMarkers);
            if (ctxEdit.selectedMarkers.length === 0) {
                treeRef.current?.deselectAll();
            } else if (ctxEdit.selectedMarkers.length === 1) {
                treeRef.current?.select(ctxEdit.selectedMarkers[0]);
            } else {
                ctxEdit.worldMarkers.forEach((marker) => {
                    if (ctxEdit.selectedMarkers.includes(marker.id)) {
                        treeRef.current?.selectMulti(marker.id);
                    } else {
                        treeRef.current.deselect(marker.id);
                    }
                });
            }
        }
    }, [ctxEdit.selectedMarkers]);

    useEffect(() => {
        setTreeData(createInitialData());
    }, [orderedMarkers.map((e) => e.id).join(';')]);

    //get height of containerRef after render
    useEffect(() => {
        if (containerRef.current) {
            setHeight(containerRef.current.clientHeight);
        }
    }, [containerRef.current?.clientHeight]);

    useEffect(() => {
        setTreeData(createInitialData());
    }, [orderedMarkers.map((e) => e.id).join(';')]);

    //get height of containerRef after render
    useEffect(() => {
        if (containerRef.current) {
            setHeight(containerRef.current.clientHeight);
        }
    }, [containerRef.current?.clientHeight]);

    return {
        onMove,
        onSelect,
        treeData,
        height,
        itemOnKeyDown,
        itemOnClick,
        containerRef,
        treeRef
    };
};
